import { GraphError } from '@microsoft/microsoft-graph-client';
import type { ConvexReactClient } from 'convex/react';
import type { SharePointExternalRef } from '@repancy/core';
import { getItem, putContent } from './sharepoint';

export type ConflictResolution = 'overwrite' | 'keep-theirs' | 'save-as-new-local';

export interface SaveBackConflict {
  kind: 'conflict';
  localContent: string;
  remoteContent: string;
  remoteEtag: string;
}

export interface SaveBackOk {
  kind: 'ok';
  newEtag: string;
}

export interface SaveBackError {
  kind: 'error';
  message: string;
}

export type SaveBackResult = SaveBackOk | SaveBackConflict | SaveBackError;

/**
 * Save Repancy-edited content back to the SharePoint item it was imported from.
 * Sends If-Match with the stored etag; surfaces 412/409 as a conflict result so
 * the caller can prompt the user (overwrite vs keep-theirs vs save-as-new-local).
 *
 * The caller is responsible for persisting the new etag via
 * api.documents.updateExternalEtag once it's happy with the result.
 */
export async function saveBack(
  externalRef: SharePointExternalRef,
  newContent: string,
): Promise<SaveBackResult> {
  try {
    const updated = await putContent(
      externalRef.driveId,
      externalRef.itemId,
      newContent,
      externalRef.etag,
    );
    if (!updated.eTag) {
      return { kind: 'error', message: 'Graph returned no etag after PUT' };
    }
    return { kind: 'ok', newEtag: updated.eTag };
  } catch (err) {
    const status = err instanceof GraphError ? err.statusCode : undefined;
    // 412 Precondition Failed (etag mismatch) and 409 Conflict are both the
    // "someone else edited this" path. Pull the remote content so the caller
    // can show a diff in the conflict dialog.
    if (status === 412 || status === 409) {
      try {
        const remote = await getItem(externalRef.driveId, externalRef.itemId);
        const remoteEtag = remote.eTag ?? '';
        return {
          kind: 'conflict',
          localContent: newContent,
          remoteContent: '', // Caller hydrates via getContent() if it wants a full diff.
          remoteEtag,
        };
      } catch (innerErr) {
        return {
          kind: 'error',
          message: innerErr instanceof Error ? innerErr.message : String(innerErr),
        };
      }
    }
    return { kind: 'error', message: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Resolve a conflict according to the user's choice. Overwrite sends the local
 * content again with the *new* remote etag as If-Match. Keep-theirs is a no-op
 * on Graph (caller should refresh the local doc from getContent). Save-as-new-local
 * also no-ops Graph; caller drops externalRef on the new local doc.
 */
export async function resolveConflict(
  externalRef: SharePointExternalRef,
  resolution: ConflictResolution,
  localContent: string,
  remoteEtag: string,
): Promise<SaveBackResult> {
  if (resolution === 'overwrite') {
    const updated = await putContent(
      externalRef.driveId,
      externalRef.itemId,
      localContent,
      remoteEtag,
    );
    if (!updated.eTag) {
      return { kind: 'error', message: 'Graph returned no etag after overwrite' };
    }
    return { kind: 'ok', newEtag: updated.eTag };
  }
  if (resolution === 'keep-theirs') {
    return { kind: 'ok', newEtag: remoteEtag };
  }
  // save-as-new-local: caller drops externalRef and inserts a local doc. Nothing to call on Graph.
  return { kind: 'ok', newEtag: remoteEtag };
}

// Re-export the Convex client type for callers chaining audit/etag-update steps.
export type { ConvexReactClient };
